// @givmo/app-charity — the claim wizard's ENTRY: two doors in front of the ceremony (CPA-WEB52 +
// CPA-WEB53), the first of them asked of a REFRESHED memberships list (CPA-WEB55).
//
// `/claim/$charityId` mounts THIS, and this mounts `ClaimWizardPage` — the ceremony — only once both
// doors have let the caller through. The page stays the ceremony alone (its two unit suites render it
// bare, with no provider around it); the doors need the active-org provider, the auth context and the
// query cache, so they live here and are pinned through the real route tree
// (`router/claim-wizard-entry.test.tsx`).
//
// ══ THE DECISION, IN ORDER ═════════════════════════════════════════════════════════════════════
//
//   0. A CACHED TERMINAL RESULT WINS (CPA-WEB51). A claimant who just submitted must see the success
//      panel and the acceptance confirmation even though the membership the submit installed now
//      exists — so the entry reads `claimTerminalResultKey(charityId)` exactly as the page does (a
//      `skipToken` observer, never a fetch) and, finding one, stands aside. The page paints the panel
//      in the same commit the chrome swap remounts it in, as the WEB51 pins require.
//   1. ALREADY ADMINISTERED → THE CONSOLE, NOT THE FORM (CPA-WEB52). The wizard used to consult no
//      memberships at all, so once the cached terminal result expired (the app's 5-minute `gcTime`)
//      a return visit to a charity the caller already holds rendered the attestation form (bundled
//      arm: the whole ceremony) again. The backend answers such a re-submit idempotently, so it was
//      friction, not a consent defect — but the person was asked to claim what they already have.
//      ⚠ The list is REFRESHED before the door decides (CPA-WEB55): the provider's cache carries a
//      15s stale window, and an Owner installed ELSEWHERE inside it was shown the form for the same
//      reason, one window later. The door reads the provider's list (`useActiveOrg`) but does NOT
//      ask for the refresh itself: `<ClaimWizardRefreshProbe>` (`claim-memberships-refresh.tsx`),
//      mounted ONCE by the authenticated layout ABOVE the portal-entry gate and the org-scoped
//      boundary, holds the one `refetchOnMount: 'always'` observer of the same query and publishes
//      `isFetchedAfterMount` into the query cache; this entry reads that fact (`refreshQuery`,
//      below) and waits on it. The asking lives up there because BOTH the gate's chrome swap and
//      the boundary's re-key REMOUNT this entry — an observer here was measured asking twice. The
//      probe skips (publishing `true` at once) when the cached list already holds this charity or
//      a terminal result outranks the door, so no request is spent where none is owed. A held
//      charity is entered through `useEnterOrganization`
//      (`org/enter-organization.ts`) — SELECTED through the context's one writer, then the doorway
//      (`DEFAULT_AUTHENTICATED_ROUTE`) runs the ONE landing rule for it — the organization deep
//      link's own mechanism, no second literal. Two steps across two renders, by construction:
//      selecting re-keys `<OrgScopedBoundary>` and remounts this entry, whose fresh instance finds
//      the target active and navigates. While it decides the caller sees the wizard's loading card,
//      never the form. A non-member, an errored read with no list in hand, and a caller who
//      administers nothing all fall through to door 2 untouched (`loaded-empty` is the portal-entry
//      gate's to route, and the gate lets `/claim/*` through in every chrome).
//   2. A PERSONAL-EMAIL ADDRESS IS REFUSED (CPA-WEB53, D-CP-63 — belt and braces). The sign-up form
//      refuses a personal domain before an account exists; this door catches the accounts that
//      exist anyway — made before the block, or through the consumer app, or by a provider sign-in
//      with a personal address. The signed-in address is checked through the SAME server read
//      (`features/auth/personal-email-block.ts`; this app carries no list), and a blacklisted
//      domain gets Jonathan's message verbatim in place of the form — the loading card while the
//      answer is in flight, never the form. `CANNOT_CLAIM`, takeover and pending-review states are
//      untouched: they are answers to a SUBMIT, and a refused caller never submits. The backend's
//      Tier-2 machinery is untouched too — it is what support uses for the manual avenue the
//      message names. Door 1 outranks this one: a member whose account happens to carry a personal
//      address (claimed through Tier-2 before the block) is entering an organization they already
//      administer, and is not told to change their address for it. The read is ASKED in parallel
//      with door 1's refresh and DECIDES after it — never the refusal or the form a round-trip
//      later than before the refresh existed.
//
// Nothing here changes what the ceremony does once it renders, on either arm.

import { skipToken, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button, testIdProp } from '@givmo/design-system';
import { Mail } from 'lucide-react';
import { useAuth } from '../../auth/auth-context';
import { useActiveOrg } from '../../org/active-org-context';
import { useEnterOrganization } from '../../org/enter-organization';
import { personalEmailBlockQueryOptions } from '../auth/personal-email-block';
import { PERSONAL_EMAIL_BLOCK_MESSAGE } from '../../lib/funnel-copy';
import { CLAIM_ROUTES } from './claim-routes';
import { claimMembershipsRefreshedKey } from './claim-memberships-refresh';
import { claimTerminalResultKey } from './claim-terminal-result';
import {
  ClaimWizardPage,
  TerminalPanel,
  WizardLoadingCard,
  WizardShell,
} from './claim-wizard-page';

const PREFIX = 'claim-wizard';

export function ClaimWizardEntry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { charityId } = useParams({ from: '/authenticated/claim/$charityId' });
  const { memberships, membershipsState } = useActiveOrg();

  // Door 0 — the page's own cached terminal result, read the way the page reads it.
  const terminalQuery = useQuery({
    queryKey: claimTerminalResultKey(charityId),
    queryFn: skipToken,
    staleTime: Infinity,
  });
  const hasTerminal = terminalQuery.data !== undefined;

  // The REFRESH in front of door 1 (CPA-WEB55) — asked by `<ClaimWizardRefreshProbe>`, which the
  // authenticated layout mounts ABOVE the portal-entry gate and the org-scoped boundary because both
  // of them REMOUNT this entry; an observer here would ask twice. Read the way door 0 reads its own
  // cached fact: a `skipToken` observer over a key nothing fetches. `false`/absent means the probe
  // has not answered yet — including the case where it has nothing to ask and says so at once.
  // ⚠ Without that probe mounted the wizard never leaves its loading card (see that file's header).
  const refreshQuery = useQuery({
    queryKey: claimMembershipsRefreshedKey(charityId),
    queryFn: skipToken,
    staleTime: Infinity,
  });
  const refreshed = refreshQuery.data === true;

  // Door 1 — held ⇒ enter the console. Only a RECOGNISED, populated list can say "held" (`ready`),
  // and only once the refresh above has answered; a pending read is waited on, and every other state
  // falls through to door 2.
  const cachedHolds =
    membershipsState === 'ready' && memberships.some((m) => m.charity_id === charityId);
  const holds = refreshed && cachedHolds;
  const entering = !hasTerminal && holds;
  useEnterOrganization(charityId, entering);

  // Door 2 — the signed-in address. ASKED as soon as door 0 has declined and the list in hand does
  // not already hold the charity — in PARALLEL with door 1's refresh, never after it: asked only
  // once the refresh had answered, the refusal and the form arrived a memberships round-trip later
  // than before CPA-WEB55 (the hosted runner surfaced it as the WEB53 pin's `findBy` expiring). It
  // DECIDES only once door 1 has declined (`waitingOnDoors`, below): a member being forwarded never
  // sees the refusal, and the answer to a request door 1 outranks is simply discarded. A claimant
  // reading their result (door 0), a caller whose list already holds the charity, and a cold load
  // still awaiting the provider's read spend no request. An account with no address has nothing to
  // check (and cannot instant-verify anyway).
  const email = user?.email ?? null;
  const blockQuery = useQuery({
    ...personalEmailBlockQueryOptions(email ?? ''),
    enabled: email !== null && !hasTerminal && !cachedHolds && membershipsState !== 'pending',
  });
  const verdict =
    email === null ? 'allowed' : (blockQuery.data ?? (blockQuery.isError ? 'allowed' : null));
  // The precedence is WEB52 over WEB53: nothing below renders until the refresh has answered and
  // door 1 has declined — `!refreshed` joins the wait, `entering` keeps the card while the console
  // opens, a pending provider read is waited on.
  const waitingOnDoors = hasTerminal || entering || !refreshed || membershipsState === 'pending';

  if (hasTerminal) return <ClaimWizardPage />;

  if (waitingOnDoors || verdict === null) {
    return (
      <WizardShell>
        <WizardLoadingCard />
      </WizardShell>
    );
  }

  if (verdict === 'blocked') {
    // Not a step of the ceremony — no step cue, no "Confirm you can act" heading over a refusal.
    return (
      <WizardShell stepCue={false}>
        <TerminalPanel
          testId={`${PREFIX}-personal-email`}
          tone="warning"
          icon={Mail}
          title={<>We can&rsquo;t continue with a personal email address</>}
          body={PERSONAL_EMAIL_BLOCK_MESSAGE}
          action={
            <div className="flex items-center gap-8">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void navigate({ to: CLAIM_ROUTES.search })}
                testId={`${PREFIX}-personal-email-back`}
              >
                Back to search
              </Button>
              {/* The in-app support page, reachable in every chrome (`CLAIM_FIRST_PATHS`) — the
                  same destination the not-claimable dead-end offers. */}
              <Link
                to="/support"
                className="inline-flex h-32 items-center rounded-md px-12 font-sans text-s13 font-w600 text-brand-700 outline-none transition-colors hover:bg-surface-hover focus-visible:shadow-ring motion-reduce:transition-none"
                {...testIdProp(`${PREFIX}-personal-email-support`)}
              >
                Contact support
              </Link>
            </div>
          }
        />
      </WizardShell>
    );
  }

  return <ClaimWizardPage />;
}
